import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { DataServiceService } from '../../../services/data-service.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { Customers, TrailerLocation } from '../../../interfaces';

@Component({
  selector: 'app-address-management',
  templateUrl: './address-management.component.html',
  styleUrl: './address-management.component.scss'
})
export class AddressManagementComponent implements OnInit {

  @Input() displayAddAddressDialog = false;
  @Input() selectedCustomer: any = {};
  @Input() customers: Customers[] = [];
  @Input() locations: TrailerLocation[] = [];
  @Output() displayAddAddressDialogChange = new EventEmitter<boolean>();
  @Output() addressSaved = new EventEmitter<void>();

  constructor(private dataServiceService: DataServiceService,
    private messageService: MessageService) {
  }

  newAddress: any = {};
  filteredCustomers: Customers[] = [];

  ngOnInit() {
    this.initializeComponent();
  }

  initializeComponent() {
    this.filteredCustomers = this.customers;
    this.resetNewAddressForm();
  }
  
  // Method to add new contact to new address
  addNewAddressContact() {
    if (!this.newAddress.contacts) {
      this.newAddress.contacts = [];
    }
    
    this.newAddress.contacts.push({
      contact_id: null,
      name: '',
      email: '',
      phone: ''
    });
  }

  // Method to remove contact from new address
  removeNewAddressContact(index: number) {
    if (this.newAddress.contacts && this.newAddress.contacts.length > 1) {
      this.newAddress.contacts.splice(index, 1);
    }
  }

  // Method to filter customers
  filterCustomers() {
    this.filteredCustomers = this.customers.filter(customer => {
      const location = this.locations.find(loc => loc.customer_id === customer.customer_id);
      return location !== undefined;
    });
  }

  // Method to reset the new address form
  resetNewAddressForm() {
    this.newAddress = {
      address: '',
      city: '',
      state: '',
      zipcode: '',
      notes: '',
      service_hours_start: null,
      service_hours_end: null,
      contacts: [{
        contact_id: null,
        name: '',
        email: '',
        phone: ''
      }]
    };
  }

  // Method to cancel and close dialog
  cancelAddAddress() {
    this.displayAddAddressDialog = false;
    this.displayAddAddressDialogChange.emit(false);
    this.resetNewAddressForm();
  }

 // Method to save new address
 saveNewAddress() {
  // Validate required fields
  if (!this.newAddress.address || !this.newAddress.city) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Address and city are required'
    });
    return;
  }

  // Prepare address data for backend with create_new_address flag
  const addressToCreate = {
    name: this.selectedCustomer.name, // Keep customer name the same
    status: this.selectedCustomer.status,
    customer_type: this.selectedCustomer.customer_type,
    // Primary contact from first contact in new address or fallback to existing
    contact_person: this.newAddress.contacts?.[0]?.name?.trim() || this.selectedCustomer.contact_person || null,
    email: this.newAddress.contacts?.[0]?.email?.trim() || this.selectedCustomer.email || null,
    phone: this.newAddress.contacts?.[0]?.phone?.trim() || this.selectedCustomer.phone || null,
    // New address fields
    address: this.newAddress.address?.trim(),
    city: this.newAddress.city?.trim(),
    state: this.newAddress.state?.trim(),
    zipcode: this.newAddress.zipcode?.trim(),
    notes: this.newAddress.notes?.trim() || '',
    service_hours_start: this.newAddress.service_hours_start?.trim() || null,
    service_hours_end: this.newAddress.service_hours_end?.trim() || null,
    // Keep existing relationships
    delivers_to: this.selectedCustomer.delivers_to || [],
    receives_from: this.selectedCustomer.receives_from || [],
    // NEW: Flag to tell backend to create new address instead of updating existing
    create_new_address: true
  };

  this.dataServiceService.updateCustomer(this.selectedCustomer.customer_id, addressToCreate)
    .subscribe({
      next: (response) => {
        this.displayAddAddressDialog = false;
        this.displayAddAddressDialogChange.emit(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'New address added successfully'
        });

        // Reset the new address form
        this.resetNewAddressForm();
        
        // Emit event to parent to refresh data
        this.addressSaved.emit();
      },
      error: (error) => {
        console.error('Address creation error:', error);
        let errorMessage = 'Failed to add address';

        if (error.error?.detail) {
          errorMessage = error.error.detail;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage
        });
      }
    });
}
  
}
